/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/**
 * Finds personal data in text: the things PDPA asks an organisation to protect
 * and that turn up in ordinary documents.
 *
 * This is rules, not a model. It runs in the browser on text the browser already
 * holds, so it works with the AI switched off and nothing is sent anywhere. A
 * number only counts when it checks out — a Thai national ID must pass its check
 * digit, a card number must pass Luhn — which is what keeps a reference number
 * or an invoice total from being flagged.
 *
 * It finds candidates for a person to review; it does not promise to find
 * everything. Names, addresses and free-text details are not covered.
 */

export type PiiKind = 'thaiId' | 'card' | 'phone' | 'email' | 'bankAccount';

/** In the order they claim text: a number that is a valid ID is not also a phone or a card. */
export const PII_KINDS: readonly PiiKind[] = ['thaiId', 'card', 'phone', 'email', 'bankAccount'];

export interface PiiMatch {
  kind: PiiKind;
  /** Offsets into the text that was searched. */
  start: number;
  end: number;
  text: string;
}

/** Thai digits ๐-๙ as ASCII ones. One character each, so every offset still points at the same place. */
export function asciiDigits(text: string): string {
  return text.replace(/[๐-๙]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x0e50 + 48));
}

/** A 13-digit Thai national ID: the last digit is a check over the first twelve. */
export function validThaiId(digits: string): boolean {
  if (!/^[1-9]\d{12}$/.test(digits)) {
    return false;
  }
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(digits[i]) * (13 - i);
  }
  return (11 - (sum % 11)) % 10 === Number(digits[12]);
}

/** The Luhn check that payment card numbers carry. */
export function luhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = Number(digits[i]);
    if (double) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
    double = !double;
  }
  return sum % 10 === 0;
}

const SEP = '[- \\u00a0]?';

const THAI_ID = new RegExp(`(?<!\\d)(\\d)${SEP}(\\d{4})${SEP}(\\d{5})${SEP}(\\d{2})${SEP}(\\d)(?!\\d)`, 'g');
const CARD = /(?<![\d-])(?:\d[- \u00a0]?){12,18}\d(?![\d-])/g;
// 06/08/09 mobiles, 02 Bangkok, 03–07 provincial; with or without the country code
const PHONE = new RegExp(
  `(?<![\\d-])(?:\\+66${SEP}|0)(?:[689]\\d${SEP}\\d{3}${SEP}\\d{4}|2${SEP}\\d{3}${SEP}\\d{4}|[3-7]\\d${SEP}\\d{3}${SEP}\\d{3})(?![\\d-])`,
  'g'
);
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
// An account number alone is just digits; it counts when the text says it is one.
const BANK = /(?:บัญชี|account|a\/c|acct)[^\d\n]{0,25}((?:\d[- \u00a0]?){8,14}\d)(?!\d)/gi;

/**
 * Personal data in `text`, in text order, each place claimed once. `kinds` limits
 * what is looked for.
 */
export function detectPii(text: string, kinds: readonly PiiKind[] = PII_KINDS): PiiMatch[] {
  const source = asciiDigits(text);
  const found: PiiMatch[] = [];

  const claimed = (start: number, end: number) => found.some((m) => start < m.end && end > m.start);
  const take = (kind: PiiKind, start: number, end: number) => {
    if (!claimed(start, end)) {
      found.push({ kind, start, end, text: text.slice(start, end) });
    }
  };
  const digitsOf = (s: string) => s.replace(/\D/g, '');

  for (const kind of PII_KINDS.filter((k) => kinds.includes(k))) {
    switch (kind) {
      case 'thaiId':
        for (const m of source.matchAll(THAI_ID)) {
          if (validThaiId(digitsOf(m[0]))) {
            take(kind, m.index, m.index + m[0].length);
          }
        }
        break;
      case 'card':
        for (const m of source.matchAll(CARD)) {
          const digits = digitsOf(m[0]);
          if (digits.length >= 13 && digits.length <= 19 && /^[2-6]/.test(digits) && luhn(digits)) {
            take(kind, m.index, m.index + m[0].length);
          }
        }
        break;
      case 'phone':
        for (const m of source.matchAll(PHONE)) {
          take(kind, m.index, m.index + m[0].length);
        }
        break;
      case 'email':
        for (const m of source.matchAll(EMAIL)) {
          take(kind, m.index, m.index + m[0].length);
        }
        break;
      case 'bankAccount':
        for (const m of source.matchAll(BANK)) {
          // the number, not the word before it
          const start = m.index + m[0].lastIndexOf(m[1]);
          take(kind, start, start + m[1].length);
        }
        break;
    }
  }

  return found.sort((a, b) => a.start - b.start);
}
